// frontend/src/pages/ResetPassword.jsx

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sessionService, sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { translateRequestError } from '../queries/api';
import { ICON, CheckCircle, Eye, EyeSlash } from '../icons';
import '../components/login/Login.css';

const STATE = {
  VERIFYING: 'verifying',
  FORM: 'form',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  INVALID: 'invalid',
};

export default function ResetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState(STATE.VERIFYING);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [theme] = useState(() => localStorage.getItem('theme') || 'light');

  const logoSrc = '/images/manexys-logo.png';

  const requirements = useMemo(() => ({
    minLength: password.length >= 8,
    notNumeric: password.length > 0 && !/^\d+$/.test(password),
    hasLetterAndNumber: /[a-zA-Z]/.test(password) && /\d/.test(password),
    match: confirmPassword.length > 0 && password === confirmPassword,
  }), [password, confirmPassword]);

  const canSubmit = requirements.minLength && requirements.notNumeric && requirements.match && password.length > 0;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    // verifyToken declared later in same scope; closure-captured at first render. Hoisting is the proper fix; flagged as a separate refactor.
    // eslint-disable-next-line react-hooks/immutability
    verifyToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyToken = async () => {
    try {
      await sessionService.initCSRF();

      const response = await sessionApi.post('/auth/password-reset/verify/', { uid, token });
      // 200 is a valid link and 400 an invalid or expired one
      // (authentication/views/auth.py verify_reset_token_view).
      if (response.ok) { setState(STATE.FORM); return; }
      if (response.status === 400) { setState(STATE.INVALID); return; }
      throw await apiErrorFrom(response, 'POST /auth/password-reset/verify/');
    } catch (err) {
      // A link that could not be CHECKED is not an invalid one. The form opens
      // with the reason; completing it verifies the token again.
      console.error('Reset link verification failed:', err.message);
      setError(translateRequestError(err, t, 'resetPassword.errors.failed'));
      setState(STATE.FORM);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (!canSubmit) return;

    setState(STATE.SUBMITTING);

    try {
      const response = await sessionApi.post('/auth/password-reset/complete/', {
        uid,
        token,
        password,
        confirm_password: confirmPassword,
      });
      if (!response.ok) throw await apiErrorFrom(response, 'POST /auth/password-reset/complete/');
      setState(STATE.SUCCESS);
    } catch (err) {
      console.error('Password reset failed:', err.message);
      // The endpoint's refusals carry no code (authentication/views/auth.py):
      // a 400 with `errors` is per field, a bare 400 the token itself, a 429
      // the IP limit. The server's message is dev-facing.
      if (err.status === 400 && err.body?.errors) {
        setFieldErrors(err.body.errors);
      } else if (err.status === 400) {
        setState(STATE.INVALID);
        return;
      } else if (err.status === 429) {
        setError(t('apiErrors.rate_limit_exceeded'));
      } else {
        setError(translateRequestError(err, t, 'resetPassword.errors.failed'));
      }
      setState(STATE.FORM);
    }
  };

  const isFormDisabled = state === STATE.SUBMITTING;

  return (
    <div className="login-container">

      {/* Left: brand panel */}
      <div className="login-brand">
        <div className="login-brand-content">
          <img src={logoSrc} alt="Manexys" className="login-logo" />
          <div className="login-brand-accent" />
          <p className="login-tagline">Forecast with Precision.<br />Execute with Confidence.</p>
          <ul className="login-features">
            <li>AI-powered demand forecasting</li>
            <li>Smart inventory optimization</li>
            <li>Automated purchase orders</li>
          </ul>
        </div>
        <div className="login-brand-footer">
          <span>manexys.com</span>
        </div>
      </div>

      {/* Right: form panel */}
      <div className="login-panel">
        <div className="login-form-wrap">

          {/* VERIFYING STATE */}
          {state === STATE.VERIFYING && (
            <div className="login-initializing">
              <div className="login-spinner" />
              <div className="login-initializing-text">{t('resetPassword.verifying')}</div>
            </div>
          )}

          {/* INVALID TOKEN STATE */}
          {state === STATE.INVALID && (
            <>
              <h1 className="login-title">{t('resetPassword.errors.invalidLink')}</h1>
              <div className="login-info login-info-error">
                {t('resetPassword.errors.invalidLinkMessage')}
              </div>
              <button
                className="login-button"
                onClick={() => navigate('/forgot-password')}
              >
                {t('resetPassword.requestNewLink')}
              </button>
            </>
          )}

          {/* SUCCESS STATE */}
          {state === STATE.SUCCESS && (
            <div className="setup-success">
              <div className="setup-success-icon">
                <CheckCircle size={ICON.display} />
              </div>
              <h1 className="login-title" style={{ textAlign: 'center' }}>
                {t('resetPassword.success.title')}
              </h1>
              <p className="login-subtitle" style={{ textAlign: 'center' }}>
                {t('resetPassword.success.message')}
              </p>
              <button
                className="login-button"
                onClick={() => navigate('/login')}
              >
                {t('resetPassword.success.signIn')}
              </button>
            </div>
          )}

          {/* FORM STATE */}
          {(state === STATE.FORM || state === STATE.SUBMITTING) && (
            <>
              <h1 className="login-title">{t('resetPassword.title')}</h1>
              <p className="login-subtitle">{t('resetPassword.subtitle')}</p>

              {error && (
                <div className="login-info login-info-error">{error}</div>
              )}

              <form className="login-form" onSubmit={handleSubmit}>
                {/* Password field */}
                <div className="login-input-group">
                  <label className="login-form-label">{t('resetPassword.password')}</label>
                  <div className="login-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="login-input"
                      disabled={isFormDisabled}
                      autoComplete="new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isFormDisabled}
                      className="login-input-icon-btn"
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    >
                      {showPassword ? <EyeSlash size={ICON.xl} /> : <Eye size={ICON.xl} />}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <div className="setup-field-error">
                      {Array.isArray(fieldErrors.password)
                        ? fieldErrors.password.join(' ')
                        : fieldErrors.password
                      }
                    </div>
                  )}
                </div>

                {/* Confirm password field */}
                <div className="login-input-group">
                  <label className="login-form-label">{t('resetPassword.confirmPassword')}</label>
                  <div className="login-input-wrapper">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="login-input"
                      disabled={isFormDisabled}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      disabled={isFormDisabled}
                      className="login-input-icon-btn"
                      aria-label={showConfirm ? t('login.hidePassword') : t('login.showPassword')}
                    >
                      {showConfirm ? <EyeSlash size={ICON.xl} /> : <Eye size={ICON.xl} />}
                    </button>
                  </div>
                  {fieldErrors.confirm_password && (
                    <div className="setup-field-error">
                      {Array.isArray(fieldErrors.confirm_password)
                        ? fieldErrors.confirm_password.join(' ')
                        : fieldErrors.confirm_password
                      }
                    </div>
                  )}
                </div>

                {/* Password requirements checklist */}
                <ul className="setup-requirements">
                  <li className={password.length > 0 ? (requirements.minLength ? 'met' : 'unmet') : ''}>
                    {t('resetPassword.requirements.minLength')}
                  </li>
                  <li className={password.length > 0 ? (requirements.notNumeric ? 'met' : 'unmet') : ''}>
                    {t('resetPassword.requirements.notNumeric')}
                  </li>
                  <li className={password.length > 0 ? (requirements.hasLetterAndNumber ? 'met' : 'unmet') : ''}>
                    {t('resetPassword.requirements.hasLetterAndNumber')}
                  </li>
                  <li className={confirmPassword.length > 0 ? (requirements.match ? 'met' : 'unmet') : ''}>
                    {t('resetPassword.requirements.match')}
                  </li>
                </ul>

                <button
                  type="submit"
                  className="login-button"
                  aria-busy={isFormDisabled}
                  disabled={!canSubmit || isFormDisabled}
                >
                  {t('resetPassword.resetPassword')}
                </button>
              </form>
            </>
          )}

        </div>
      </div>

    </div>
  );
}
